import DateFnsUtils from '@date-io/date-fns';
import { makeStyles } from '@material-ui/core';
import {
    DatePicker as MuiDatePicker,
    DatePickerProps,
    DateTimePicker as MuiDateTimePicker,
    DateTimePickerProps,
    MuiPickersUtilsProvider,
    TimePicker as MuiTimePicker,
    TimePickerProps,
} from '@material-ui/pickers';
import clsx from 'clsx';
import React from 'react';
import { Calendar, Clock } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Input } from './Input';

const useDateTimeClasses = makeStyles({
    dialog: {
        '& .MuiDialogContent-root': {
            color: theme.colors.neutral[1],
            '& .MuiToolbar-root': {
                background: theme.colors.neutral[1],
                color: theme.colors.white,
            },
            // date-year picker classes
            '& .MuiPickersYear-root, & .MuiPickersYear-yearSelected': {
                color: theme.colors.neutral[1],
            },
            // date-day picker classes
            '& .MuiPickersDay-day': {
                color: theme.colors.neutral[1],
            },
            '& .MuiPickersDay-daySelected': {
                background: theme.colors.neutral[1],
                color: theme.colors.white,
            },
            // time picker classes
            '& .MuiPickersClock-pin, & .MuiPickersClockPointer-pointer': {
                background: theme.colors.neutral[1],
            },
            '& .MuiPickersClockPointer-thumb': {
                background: theme.colors.white,
                borderColor: theme.colors.neutral[1],
            },
            '& .MuiPickersClockNumber-clockNumber': {
                color: theme.colors.neutral[2],
            },
            '& .MuiPickersClockNumber-clockNumberSelected': {
                background: theme.colors.neutral[1],
                color: theme.colors.white,
            },
            // date time picker classes
            '& .MuiTabs-root': {
                background: theme.colors.neutral[1],
                '& .MuiTabs-indicator': {
                    background: theme.colors.neutral[2],
                },
            },
        },
        '& .MuiDialogActions-root': {
            '& .MuiButton-text': {
                color: theme.colors.neutral[1],
                '&:hover': {
                    background: 'rgba(0, 0, 0, 0.05)',
                },
            },
        },
    },
});

/**
 * Themed material-ui date picker.
 *
 * @param props.color enforced type
 * @param props.inputVariant enforced type
 */
export const DatePicker = (
    props: {
        type?: 'date' | 'time' | 'datetime';
        date: Date | undefined;
        onDate: (date: Date | undefined) => void;
        clearable?: boolean;
        color?: never;
        pickerProps?: DatePickerProps | TimePickerProps | DateTimePickerProps;
    } & Parameters<typeof Input>[0],
) => {
    const { type = 'date', date, clearable, onDate, pickerProps, ...inputProps } = props;
    const classes = useDateTimeClasses();
    const { t } = usePrefixTranslation('global', { global: 'forms.DateTime' });
    const [open, setOpen] = React.useState(false);
    const Picker = type === 'date' ? MuiDatePicker : type === 'time' ? MuiTimePicker : MuiDateTimePicker;
    const cls = { open };

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Picker
                {...(pickerProps as any)}
                value={date ? date : null}
                onChange={date => onDate(date ? date : undefined)}
                labelFunc={date => (date ? t(`picker-${type}`, { date }) : '')}
                clearable={clearable}
                DialogProps={{ ...pickerProps?.DialogProps, className: classes.dialog, disableScrollLock: true }}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                TextFieldComponent={props => (
                    <Input
                        {...inputProps}
                        readOnly
                        value={props.value as string}
                        insideProps={{
                            className: clsx(cls, inputProps.insideProps?.className),
                            onClick: props.onClick,
                            onKeyDown: props.onKeyDown,
                        }}
                        right={
                            <>
                                {type.includes('date') && <Calendar />}
                                {type.includes('time') && <Clock />}
                            </>
                        }
                    />
                )}
            />
        </MuiPickersUtilsProvider>
    );
};
